import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { FormattedMessage } from 'react-intl';
import { Box, makeStyles, Typography } from '@popmenu/common-ui';
import { useRootPlaceBySlugData } from '../../hooks/data/useRootPlaceBySlugData';
import { DishCardsSkeleton } from '../DishCardsSkeleton';
import { DishFeedCard } from '../DishFeedCard';

type DishFeedScrollerProps = {
  dishPathPrefix: string
  placeSlug: string
  rootDishCategorySlug?: string | null
}

const useStyles = makeStyles(theme => ({
  cardContainer: {
    [theme.breakpoints.down('md')]: {
      flex: `0 1 calc(33.33% - ${theme.spacing(3)}px)`,
    },
    [theme.breakpoints.down('sm')]: {
      flex: `0 1 calc(50% - ${theme.spacing(3)}px)`,
    },
    [theme.breakpoints.down('xs')]: {
      flex: '0 1 100%',
    },
    flex: `0 1 calc(25% - ${theme.spacing(3)}px)`,
    marginBottom: theme.spacing(6),
  },
  cardSkeleton: {
    borderRadius: theme.spacing(1),
    height: 0,
    paddingTop: '75%',
  },
  container: {
    // Only show 1 row of skeleton cards based on screen size
    [theme.breakpoints.down('md')]: {
      '& .infinite-skeleton:nth-of-type(n+4)': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('sm')]: {
      '& .skeleton:nth-of-type(n+3)': {
        display: 'none',
      },
    },
    [theme.breakpoints.down('xs')]: {
      '& .infinite-skeleton:nth-of-type(n+2)': {
        display: 'none',
      },
    },
    '& > div': {
      alignItems: 'flex-start',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
  },
  noResultsContainer: {
    padding: theme.spacing(8, 4),
    textAlign: 'center',
    width: '100%',
  },
}));

export const DishFeedScroller = ({ dishPathPrefix, placeSlug, rootDishCategorySlug = null }: DishFeedScrollerProps) => {
  const classes = useStyles();

  const limit = 12;
  const sortBy = 'popsCount';
  const sortDir = 'desc';
  const { data: placeData, fetchMore } = useRootPlaceBySlugData({
    limit,
    offset: 0,
    rootDishCategorySlug,
    slug: placeSlug,
    sortBy,
    sortDir,
  });
  const place = placeData?.rootPlace;
  const hasMore = place?.menuItems?.hasMore ?? true;
  const offset = place?.menuItems?.offset ?? 0;

  if (!place) {
    return (
      <Box className={classes.container}>
        <div>
          <DishCardsSkeleton
            cardContainerClassName={classes.cardContainer}
            cardSkeletonClassName={classes.cardSkeleton}
            length={limit}
          />
        </div>
      </Box>
    );
  }
  return (
    <Box className={classes.container}>
      <InfiniteScroll
        hasMore={hasMore}
        initialLoad={false}
        loadMore={() => {
          void fetchMore({
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const mergedRecords = [
                ...previousResult.rootPlace.menuItems.records,
                ...fetchMoreResult.rootPlace.menuItems.records,
              ];
              const finalResult = fetchMoreResult;
              finalResult.rootPlace.menuItems.records = mergedRecords;
              return finalResult;
            },
            variables: {
              pagination: {
                limit,
                offset: offset + limit,
                sortBy,
                sortDir,
              },
              slug: placeSlug,
            },
          });
        }}
        loader={(
          <DishCardsSkeleton
            cardContainerClassName={classes.cardContainer}
            cardSkeletonClassName={classes.cardSkeleton}
            className="infinite-skeleton"
            length={4}
          />
        )}
        useWindow
      >
        <React.Fragment>
          {place.menuItems.records.length === 0 && (
            <Box className={classes.noResultsContainer}>
              <Typography>
                <FormattedMessage
                  id="dish_feed_scroller.no_results"
                  defaultMessage="Sorry, dishes are temporarily unavailable for {placeName}."
                  values={{
                    placeName: place.name,
                  }}
                />
              </Typography>
            </Box>
          )}
          {place.menuItems.records.map(item => (
            <Box key={item.id} className={classes.cardContainer} component="section">
              <DishFeedCard
                {...item}
                dishPathPrefix={dishPathPrefix}
              />
            </Box>
          ))}
        </React.Fragment>
      </InfiniteScroll>
    </Box>
  );
};
